import { useState, useCallback } from "react";
import { GripVertical, X, Minus, Maximize2 } from "lucide-react";
import { cn } from "../../lib/utils";
import { getZIndex, ZIndexLayer } from "../../lib/z-index";
import { Button } from "../button";
import { useDraggableWindow, Position, InitialPosition } from "./useDraggableWindow";
import { useRegisterInteractiveWindow } from "../../context/InteractiveWindowContext";

export interface DraggableWindowProps {
  /** Whether the window is visible */
  open?: boolean;
  /** Callback when open state changes (e.g., close button clicked) */
  onOpenChange?: (open: boolean) => void;
  /** Window title displayed in header */
  title?: string;
  /** Initial position (defaults to bottom-right of viewport) */
  initialPosition?: InitialPosition;
  /** LocalStorage key for persisting position */
  persistKey?: string;
  /** Whether the window can be minimized */
  minimizable?: boolean;
  /** Whether the window starts minimized */
  defaultMinimized?: boolean;
  /** Controlled minimized state */
  minimized?: boolean;
  /** Callback when minimized state changes */
  onMinimizedChange?: (minimized: boolean) => void;
  /** Z-index layer from the z-index system */
  zIndexLayer?: ZIndexLayer;
  /** Custom z-index value (overrides zIndexLayer) */
  zIndex?: number;
  /** Window width */
  width?: number | string;
  /** Window height (content area) */
  height?: number | string;
  /** When true, the window fills the entire viewport and dragging is disabled */
  fullscreen?: boolean;
  /** Whether to constrain window within viewport */
  constrainToViewport?: boolean;
  /** Padding from viewport edges when constrained */
  viewportPadding?: number;
  /** Called when window position changes */
  onPositionChange?: (position: Position) => void;
  /** Custom header content (replaces default header if provided) */
  header?: React.ReactNode;
  /** Custom header actions (added alongside default close/minimize buttons) */
  headerActions?: React.ReactNode;
  /** Whether to show close button */
  showCloseButton?: boolean;
  /** Content */
  children: React.ReactNode;
  /** Additional className for the window container */
  className?: string;
  /** Additional className for the content area */
  contentClassName?: string;
}

/**
 * DraggableWindow - A floating window that can be dragged around the viewport.
 *
 * Features:
 * - Native mouse event drag handling (no external library)
 * - Position persistence via localStorage
 * - Viewport boundary constraints
 * - Minimize/maximize support
 * - Configurable header with drag handle
 * - Z-index integration with the design system
 *
 * @example
 * ```tsx
 * <DraggableWindow
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   title="My Window"
 *   persistKey="my-window"
 *   minimizable
 *   width={360}
 * >
 *   <div className="p-4">Window content here</div>
 * </DraggableWindow>
 * ```
 */
export function DraggableWindow({
  open = true,
  onOpenChange,
  title,
  initialPosition = "bottom-right",
  persistKey,
  minimizable = false,
  defaultMinimized = false,
  minimized: controlledMinimized,
  onMinimizedChange,
  zIndexLayer = "floatingWindow",
  zIndex: customZIndex,
  width = 320,
  height,
  fullscreen = false,
  constrainToViewport = true,
  viewportPadding = 10,
  onPositionChange,
  header,
  headerActions,
  showCloseButton = true,
  children,
  className,
  contentClassName,
}: DraggableWindowProps) {
  // Minimized state - can be controlled or uncontrolled
  const [internalMinimized, setInternalMinimized] = useState(defaultMinimized);
  const isMinimized = controlledMinimized !== undefined ? controlledMinimized : internalMinimized;

  const handleMinimizedChange = useCallback(
    (newMinimized: boolean) => {
      if (controlledMinimized === undefined) {
        setInternalMinimized(newMinimized);
      }
      onMinimizedChange?.(newMinimized);
    },
    [controlledMinimized, onMinimizedChange]
  );

  // Get numeric width for position calculations
  const numericWidth = typeof width === "number" ? width : 320;
  const numericHeight = typeof height === "number" ? height : 400;

  const { position, isDragging, dragHandleProps, windowRef } = useDraggableWindow({
    initialPosition,
    persistKey,
    constrainToViewport,
    viewportPadding,
    onPositionChange,
    windowWidth: numericWidth,
    windowHeight: numericHeight,
  });

  // Register this window with the interactive window context when open
  useRegisterInteractiveWindow(open);

  // Don't render if not open
  if (!open) return null;

  // Resolve z-index
  const finalZIndex = customZIndex ?? getZIndex(zIndexLayer);

  // Handle close
  const handleClose = () => {
    onOpenChange?.(false);
  };

  // Handle minimize toggle
  const handleMinimizeToggle = () => {
    handleMinimizedChange(!isMinimized);
  };

  return (
    <div
      ref={windowRef}
      data-interactive-with-dialog
      className={cn(
        "fixed bg-background rounded-lg border shadow-lg overflow-hidden",
        "transition-shadow duration-200",
        isDragging && "shadow-xl",
        fullscreen && "!rounded-none flex flex-col",
        className
      )}
      style={
        fullscreen
          ? { top: 0, left: 0, width: "100vw", height: "100vh", zIndex: finalZIndex }
          : {
              top: isMinimized ? "auto" : position.y,
              left: isMinimized ? "50%" : position.x,
              bottom: isMinimized ? viewportPadding : "auto",
              transform: isMinimized ? "translateX(-50%)" : undefined,
              width,
              zIndex: finalZIndex,
            }
      }
    >
      {/* Custom header or default header */}
      {header ?? (
        <div
          className={cn(
            "flex items-center justify-between px-3 py-2 bg-muted border-b select-none",
            fullscreen ? "cursor-default" : isDragging ? "cursor-grabbing" : "cursor-grab"
          )}
          {...(fullscreen ? {} : dragHandleProps)}
        >
          <div className="flex items-center gap-2 min-w-0">
            <GripVertical className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            {title && <span className="text-sm font-medium truncate">{title}</span>}
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            {headerActions}
            {minimizable && !fullscreen && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={handleMinimizeToggle}
                onMouseDown={(e) => e.stopPropagation()}
              >
                {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
              </Button>
            )}
            {showCloseButton && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={handleClose}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Content - hidden when minimized */}
      {(!isMinimized || fullscreen) && (
        <div
          className={cn("overflow-auto", fullscreen && "flex-1 min-h-0", contentClassName)}
          style={{ height: fullscreen ? undefined : height }}
        >
          {children}
        </div>
      )}
    </div>
  );
}
